package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.render.RenderReport
import dev.lingvo.shared.screenshots.render.effectiveLayers
import dev.lingvo.shared.screenshots.text.FitResult
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.screenshotCaptureImage
import dev.lingvo.site.api.screenshotDesignAssetImage
import dev.lingvo.site.components.studio.canvas.BitmapImage
import dev.lingvo.site.components.studio.preview.DesignLooksPort
import dev.lingvo.site.components.studio.preview.PreviewBitmapStore
import dev.lingvo.site.components.studio.preview.PreviewResources
import dev.lingvo.site.components.studio.preview.PreviewQueue
import dev.lingvo.site.components.studio.preview.PreviewRenderer
import dev.lingvo.site.components.studio.preview.Thumbnail
import dev.lingvo.site.components.studio.preview.ThumbnailCache
import dev.lingvo.site.components.studio.preview.ThumbnailKey
import dev.lingvo.site.components.studio.preview.previewBitmap
import dev.lingvo.site.components.studio.preview.previewDocumentSha
import dev.lingvo.site.components.studio.preview.previewAssetIds
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

/** Renders one queued cell per frame, with all languages measured and only the selected language decoded. */
@Composable
internal fun ShotPreviewGrid(
    data: DesignPageData, design: ScreenshotDesign, device: DeviceClass, locale: String, looks: DesignLooksPort,
    onFits: (List<CaptionFit>) -> Unit, allLocales: Boolean = true, sample: Boolean = false, priority: Boolean = false, firstOnly: Boolean = false, sharedResources: PreviewResources? = null,
    longestFirst: Boolean = false, focusSlot: String? = null,
    /** Off in the resize preview: its links would leave an unsaved comparison, and a modal has no page of its own. */
    links: Boolean = true,
) {
    val canonical = remember(design) { DesignCodec.encode(design) }
    var sha by remember(canonical) { mutableStateOf<String?>(null) }
    var hashError by remember(canonical) { mutableStateOf(false) }
    val resources = remember(data.project.id, device, locale, looks, sharedResources) { sharedResources ?: PreviewResources(looks) }
    val cache = resources.cache
    val queue = resources.queue
    val bitmapStore = resources.images
    val renderer = resources.renderer
    DisposableEffect(resources, sharedResources) { onDispose { if (sharedResources == null) resources.close() } }
    var fits by remember(canonical, device) { mutableStateOf<Map<String, CaptionFit>>(emptyMap()) }
    LaunchedEffect(canonical) {
        try { sha = previewDocumentSha(canonical) } catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) { hashError = true }
    }
    LaunchedEffect(fits) { onFits(fits.values.toList()) }
    val board = design.boards.firstOrNull { it.deviceClass == device.id } ?: return
    val locales = if (allLocales) (listOf(data.project.sourceLocale) + data.project.locales).distinct() else listOf(locale)
    val slotIds = data.plan.slots.associate { it.key to it.id }
    val focusSlotId = slotIds[focusSlot] ?: focusSlot
    val focusId = "design-preview-${device.id}-$locale-$focusSlotId"
    // Moving even a keyed DOM node can send browser focus to body. Capture ownership before
    // composition applies the move, then restore the same slot after the DOM has settled.
    val restoreCardFocus = focusSlot != null && document.activeElement?.id == focusId
    SideEffect {
        if (restoreCardFocus && (document.activeElement == document.body || document.activeElement == null)) {
            (document.getElementById(focusId) as? HTMLElement)?.focus()
        }
    }
    var readyCards by remember(canonical, device, locale) { mutableStateOf<Set<String>>(emptySet()) }
    var focusApplied by remember(focusSlotId, device, locale) { mutableStateOf(false) }
    val focusReady = focusSlot != null && focusSlot in readyCards
    LaunchedEffect(focusSlotId, device, locale, focusReady) {
        if (focusSlot != null && focusReady && !focusApplied) (document.getElementById(focusId) as? HTMLElement)?.let { card ->
            card.focus()
            card.asDynamic().scrollIntoView(js("({block:'nearest',inline:'nearest'})"))
            focusApplied = true
        }
    }
    if (hashError) LMuted(t("app.projects.slug.screenshots.shot_preview_grid.hash_failed", "The preview couldn't start. Reload to try again."))
    Column(Modifier.gap(Space.MD)) {
        locales.forEach { language ->
            board.pages.flatMap { it.slotIds }.let { if (firstOnly) it.take(1) else it }.let { slots ->
                if (longestFirst) slots.sortedByDescending { (designCaptionKey(design, board, it, language)?.let { key -> captionValue(data, language, key) ?: captionValue(data, data.project.sourceLocale, key) }).orEmpty().length } else slots
            }.forEach { slot ->
                key(language, slotIds[slot] ?: slot) {
                    val visible = language == locale
                    Div(attrs = {
                        if (!visible) style { property("display", "none") }
                        else {
                            if (!firstOnly) attr("data-preview-slot", slot)
                            if (slot == focusSlot) { attr("id", focusId); attr("tabindex", "-1") }
                        }
                    }) {
                        sha?.let { digest ->
                            ShotPreviewCell(data, design, device, slot, language, digest, looks, renderer, queue, cache, bitmapStore,
                                decodeImages = visible, sample = sample, priority = priority, links = links,
                                onReport = { report ->
                                    fits = fits + ("$language:$slot" to CaptionFit(slot, language, report))
                                    if (visible) readyCards = readyCards + slot
                                })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ShotPreviewCell(
    data: DesignPageData, design: ScreenshotDesign, device: DeviceClass, slotKey: String, locale: String, sha: String,
    looks: DesignLooksPort, renderer: PreviewRenderer, queue: PreviewQueue, cache: ThumbnailCache, bitmapStore: PreviewBitmapStore,
    decodeImages: Boolean, sample: Boolean, priority: Boolean, onReport: (RenderReport) -> Unit, links: Boolean = true,
) {
    val board = design.boards.first { it.deviceClass == device.id }
    val ordered = board.pages.flatMap { it.slotIds }
    val slot = data.plan.slots.firstOrNull { it.key == slotKey }
    val captionKey = designCaptionKey(design, board, slotKey, locale)
    val ownCaption = captionKey?.let { captionValue(data, locale, it) }
    val captionKeyId = data.captions.values.flatten().firstOrNull { it.key.name == captionKey }?.key?.id
        ?: slot?.captionKeyId?.takeIf { captionKey == slotKey }
    val missingCaption = captionKey != null && ownCaption.isNullOrEmpty()
    val captures = ordered.map { key ->
        val planSlot = data.plan.slots.firstOrNull { it.key == key }
        val captureLocale = if (planSlot?.localizedUi == false) data.project.sourceLocale else locale
        data.captures.firstOrNull { it.slotKey == key && it.deviceClass == device && it.locale == captureLocale && it.supersededAt == null && !it.blobMissing }
    }
    val missingCapture = captures.getOrNull(ordered.indexOf(slotKey)) == null
    val inputsKey = captures.joinToString { it?.sha256.orEmpty() } + ":" + designCaptionKeys(design, board, locale).joinToString { key -> key + ":" + captionValue(data, locale, key).orEmpty() + ":" + captionValue(data, data.project.sourceLocale, key).orEmpty() } + ":$decodeImages:$sample"
    val cacheKey = ThumbnailKey(sha, slotKey, locale, device.id, inputsKey)
    val id = "$sha:$slotKey:$locale:${device.id}:$decodeImages"
    var thumbnail by remember(cacheKey) { mutableStateOf(cache.get(cacheKey)) }
    var failure by remember(cacheKey) { mutableStateOf<String?>(null) }
    var retry by remember(cacheKey) { mutableStateOf(0) }
    val bitmaps = remember(bitmapStore, captures.map { it?.sha256 }, decodeImages, sample) { mutableMapOf<String, BitmapImage>() }
    DisposableEffect(bitmaps, queue, id) {
        onDispose { queue.cancel(id); bitmaps.clear() }
    }
    LaunchedEffect(cacheKey, retry, priority, bitmapStore) {
        thumbnail?.let { onReport(it.report); return@LaunchedEffect }
        delay(if (priority) 0 else 300)
        val captions: (TextContent, Int) -> String? = { content, index ->
            when (content) {
                is TextContent.SlotCaption -> ordered.getOrNull(index)?.let { key -> captionValue(data, locale, key) ?: captionValue(data, data.project.sourceLocale, key) }
                is TextContent.Key -> captionValue(data, locale, content.key) ?: captionValue(data, data.project.sourceLocale, content.key)
                is TextContent.Literal -> content.text
            }
        }
        try {
            if (decodeImages) {
                val page = board.pages.first { slotKey in it.slotIds }
                val sources = layers(page.layers).mapNotNull { layer -> when (layer) {
                    is DeviceLayer -> layer.screen
                    is ImageLayer -> layer.source
                    else -> null
                } }
                val start = ordered.indexOf(page.slotIds.first())
                sources.filterIsInstance<ImageSource.Capture>().map { source ->
                    ((start + source.slot + source.planOffset) % ordered.size + ordered.size) % ordered.size
                }.distinct().mapNotNull { captures.getOrNull(it) }.forEach { capture ->
                    if (capture.id !in bitmaps) bitmaps[capture.id] = bitmapStore.load("capture:${capture.id}:${capture.sha256}", capture.width, capture.height) {
                        ApiClient.screenshotCaptureImage(data.project.slug, design.platform, capture.id)
                    }
                }
                previewAssetIds(page.layers, design.brand.logoAssetId).forEach { asset ->
                    if ("asset:$asset" !in bitmaps) bitmaps["asset:$asset"] = bitmapStore.load("asset:$asset") { ApiClient.screenshotDesignAssetImage(data.project.slug, design.platform, asset) }
                }
                if (sample && missingCapture && "sample" !in bitmaps) looks.sampleScreen()?.let { bitmaps["sample"] = bitmapStore.load("sample") { it } }
            }
            renderer.prepare(design, board.id, locale, captions)
            val rendered = suspendCancellableCoroutine { continuation ->
                queue.enqueue(id, priority) {
                    if (continuation.isActive) try {
                        continuation.resume(renderer.render(ghostMissingCaptions(design, board.id, data, locale), board.id, slotKey, locale, captions) { source, index ->
                            when (source) {
                                is ImageSource.Capture -> captures.getOrNull(index)?.id?.let(bitmaps::get) ?: bitmaps["sample"]
                                is ImageSource.Asset -> bitmaps["asset:${source.assetId}"]
                                ImageSource.BrandLogo -> bitmaps["asset:${design.brand.logoAssetId}"]
                            }
                        })
                    } catch (error: Throwable) { continuation.resumeWithException(error) }
                }
                continuation.invokeOnCancellation { queue.cancel(id) }
            }
            cache.put(cacheKey, rendered)
            thumbnail = rendered
            onReport(rendered.report)
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (_: Throwable) { failure = trf("app.projects.slug.screenshots.shot_preview_grid.failed", "Couldn't render {shot}. Check the images and font connection, then retry.", "shot" to (slot?.title ?: slotKey)) }
    }
    Column(Modifier.gap(Space.SM)) {
        val fitted = thumbnail?.report?.texts?.let { texts -> texts.firstOrNull { it.primaryCaption }
            ?: if (design.schemaVersion < 3) texts.firstOrNull { it.content is TextContent.SlotCaption } else null }?.fit
        val fitText = when (fitted) {
            is FitResult.Refused -> t("app.projects.slug.screenshots.shot_preview_grid.overflow", "Caption does not fit")
            null -> t("app.projects.slug.screenshots.shot_preview_grid.pending", "Checking caption fit")
            else -> tf("app.projects.slug.screenshots.shot_preview_grid.fit_px", "Caption fits at {size} px", "size" to fitted.layout.size)
        }
        thumbnail?.let { image -> Img(image.dataUrl, alt = "${slot?.title ?: slotKey}, ${localeInfo(locale).englishName}, $fitText", attrs = {
            attr("lang", locale)
            attr("width", (board.canvas.width * 240.0 / board.canvas.height).toInt().coerceAtLeast(1).toString())
            attr("height", "240")
            style { property("height", "auto"); property("max-height", "240px"); property("max-width", "100%"); property("object-fit", "contain") }
        }) }
        if (decodeImages) {
            LMuted(slot?.title ?: slotKey)
            LMuted(fitText)
            failure?.let { message ->
                LMuted(message)
                dev.lingvo.site.ds.LButton(t("app.projects.slug.screenshots.shot_preview_grid.retry", "Retry preview"), onClick = { failure = null; retry++ }, variant = dev.lingvo.site.ds.LButtonVariant.SECONDARY)
            }
            if (missingCaption) {
                LMuted(tf("app.projects.slug.screenshots.shot_preview_grid.no_caption", "No {language} caption yet", "language" to localeInfo(locale).englishName))
                if (links) LLinkButton(t("app.projects.slug.screenshots.shot_preview_grid.open_caption", "Open caption"), captionPath(data.project.slug, locale, captionKeyId), variant = LButtonVariant.SECONDARY)
                if (links) LLinkButton(t("app.projects.slug.screenshots.shot_preview_grid.pretranslate", "Pre-translate captions"), "/app/projects/${data.project.slug}/pretranslate", variant = LButtonVariant.SECONDARY)
            }
            if (missingCapture) {
                if (sample) LMuted(t("app.projects.slug.screenshots.shot_preview_grid.sample", "Sample app. Your capture appears after the first upload."))
                else {
                    LMuted(tf("app.projects.slug.screenshots.shot_preview_grid.no_capture", "No {language} capture yet", "language" to localeInfo(locale).englishName))
                    if (links) LLinkButton(t("app.projects.slug.screenshots.shot_preview_grid.upload", "Upload"), screenshotsPath(data.project.slug, ScreenshotsTab.PLAN, ScreenshotsAreaState(design.platform, device)), variant = LButtonVariant.SECONDARY)
                    if (links) LLinkButton(t("app.projects.slug.screenshots.shot_preview_grid.ask_agent", "Ask an agent"), screenshotsAgentsPath(data.project.slug, ScreenshotsAreaState(design.platform, device)), variant = LButtonVariant.SECONDARY)
                }
            }
        }
    }
}

internal fun captionValue(data: DesignPageData, locale: String, key: String): String? = data.captions[locale]?.firstOrNull { it.key.name == key }?.let {
    if (locale == data.project.sourceLocale) it.source.value else it.target?.value
}?.takeIf { it.isNotEmpty() }

internal fun ghostMissingCaptions(design: ScreenshotDesign, boardId: String, data: DesignPageData, locale: String): ScreenshotDesign =
    design.copy(boards = design.boards.map { board ->
        if (board.id != boardId) board else {
            var offset = 0
            val ordered = board.pages.flatMap { it.slotIds }
            board.copy(pages = board.pages.map { page ->
                val effective = layers(effectiveLayers(page, board, locale, design.schemaVersion)).associateBy { it.base.id }
                fun ghost(items: List<Layer>): List<Layer> = items.map { layer -> when (layer) {
                    is TextLayer -> {
                        val key = when (val content = (effective[layer.base.id] as? TextLayer)?.content ?: layer.content) {
                            is TextContent.SlotCaption -> ordered.getOrNull(offset + content.slot)
                            is TextContent.Key -> content.key
                            is TextContent.Literal -> null
                        }
                        if (key != null && captionValue(data, locale, key) == null) layer.copy(base = layer.base.copy(opacity = layer.base.opacity * 0.4)) else layer
                    }
                    is GroupLayer -> layer.copy(children = ghost(layer.children))
                    else -> layer
                } }
                page.copy(layers = ghost(page.layers)).also { offset += page.slotIds.size }
            })
        }
    })
